import { Button } from '@/ui/shadcn/button';
import type { LeadPanelProps, LeadPanelRecord } from '../../lib/leadPanels';
import { usePermission } from '../../ui';
import { Icon } from '../../ui/icons/Icon';
import styles from './LeadApproveSendPlaceholder.module.css';

/** Whether the role holds both keys the action needs, `email.manage` and `lead.manage` (F11-h spec FR-V2). */
export function useApproveSendAllowed(): boolean {
    const emailAllowed = usePermission('email.manage').kind === 'allowed';
    const leadAllowed = usePermission('lead.manage').kind === 'allowed';
    return emailAllowed && leadAllowed;
}

/**
 * The action's label on a lead in `status`: *Approve & send* on a `new` lead, *Send follow-up* on an
 * `approved` one, and `null` — no button — on an `archived` or `converted` lead (F11-h spec decision 5).
 */
export function approveSendLabel(
    status: LeadPanelRecord['status'],
): 'Approve & send' | 'Send follow-up' | null {
    if (status === 'new') {
        return 'Approve & send';
    }
    return status === 'approved' ? 'Send follow-up' : null;
}

/**
 * What the lead view's `actions` place shows while the *Approve & send* chunk loads (BUG-265): the same shadcn
 * `Button`, hidden and inert, so the heading row takes its final shape at its first paint. With nothing there,
 * the button arrived after the row had settled and wrapped *Edit* and *Archive* onto the next line,
 * so a press made as it arrived could land where *Archive* had been (lesson L-208). Rendered only
 * where the action will show, so the group of a role without the action stays `:empty` (BUG-012). Kept out of
 * the action's chunk (`src/app/leadPanels.ts` imports it directly), and holds no logic: it never takes focus,
 * a press or a screen reader's attention. `ComposeAiDraftPlaceholder` is the same stand-in for the compose
 * dialog's *AI draft*.
 */
export function LeadApproveSendPlaceholder({ lead }: LeadPanelProps) {
    const allowed = useApproveSendAllowed();
    const label = approveSendLabel(lead.status);
    if (!allowed || label === null) {
        return null;
    }
    return (
        <div
            aria-hidden="true"
            className={styles.placeholder}
            data-testid="ai-drafts-approve-send-placeholder"
            inert
        >
            <Button data-testid="ai-drafts-approve-send-placeholder-button" tabIndex={-1}>
                <Icon name="success" size="sm" />
                {label}
            </Button>
        </div>
    );
}
